export interface SendEmailParams {
  to: string[];
  subject: string;
  body: string;
  bodyType?: "text" | "html";
  cc?: string[];
  bcc?: string[];
  importance?: "low" | "normal" | "high";
  attachments?: {
    name: string;
    contentBytes: string; // base64 encoded
    contentType: string;
  }[];
}

export interface SendEmailResponse {
  id: string;
  createdDateTime: string;
  lastModifiedDateTime: string;
  webLink: string;
}

/**
 * Send an email using Microsoft Graph API
 * @param token - Microsoft Graph API access token
 * @param params - Email parameters
 * @returns Promise with the sent message details
 */
export const sendEmail = async (
  token: string,
  params: SendEmailParams
): Promise<SendEmailResponse> => {
  const {
    to,
    subject,
    body,
    bodyType = "text",
    cc = [],
    bcc = [],
    importance = "normal",
    attachments = [],
  } = params;

  // Build the message object according to Graph API format
  const message = {
    subject,
    importance,
    body: {
      contentType: bodyType === "html" ? "HTML" : "Text",
      content: body,
    },
    toRecipients: to.map((email) => ({
      emailAddress: {
        address: email,
      },
    })),
    ccRecipients: cc.map((email) => ({
      emailAddress: {
        address: email,
      },
    })),
    bccRecipients: bcc.map((email) => ({
      emailAddress: {
        address: email,
      },
    })),
    attachments: attachments.map((attachment) => ({
      "@odata.type": "#microsoft.graph.fileAttachment",
      name: attachment.name,
      contentBytes: attachment.contentBytes,
      contentType: attachment.contentType,
    })),
  };

  const response = await fetch("https://graph.microsoft.com/v1.0/me/sendMail", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      message,
      saveToSentItems: true,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Error sending email: ${response.status} ${response.statusText} - ${errorText}`
    );
  }

  // sendMail endpoint returns 202 Accepted with no body
  // If you need the message details, you can use /me/messages endpoint instead
  return {
    id: "", // sendMail doesn't return the message ID
    createdDateTime: new Date().toISOString(),
    lastModifiedDateTime: new Date().toISOString(),
    webLink: "",
  };
};

/**
 * Create a draft email using Microsoft Graph API
 * @param token - Microsoft Graph API access token
 * @param params - Email parameters
 * @returns Promise with the created draft message
 */
export const createDraftEmail = async (
  token: string,
  params: SendEmailParams
): Promise<SendEmailResponse> => {
  const {
    to,
    subject,
    body,
    bodyType = "text",
    cc = [],
    bcc = [],
    importance = "normal",
    attachments = [],
  } = params;

  const message = {
    subject,
    importance,
    body: {
      contentType: bodyType === "html" ? "HTML" : "Text",
      content: body,
    },
    toRecipients: to.map((email) => ({
      emailAddress: {
        address: email,
      },
    })),
    ccRecipients: cc.map((email) => ({
      emailAddress: {
        address: email,
      },
    })),
    bccRecipients: bcc.map((email) => ({
      emailAddress: {
        address: email,
      },
    })),
    attachments: attachments.map((attachment) => ({
      "@odata.type": "#microsoft.graph.fileAttachment",
      name: attachment.name,
      contentBytes: attachment.contentBytes,
      contentType: attachment.contentType,
    })),
  };

  const response = await fetch("https://graph.microsoft.com/v1.0/me/messages", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(message),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Error creating draft: ${response.status} ${response.statusText} - ${errorText}`
    );
  }

  const data = await response.json();
  return data as SendEmailResponse;
};
